import { useState, type ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArticleIcon from '@mui/icons-material/Article';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import SettingsIcon from '@mui/icons-material/Settings';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/auth.api';
import { useQuery } from '@tanstack/react-query';
import Chip from '@mui/material/Chip';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import { ADMIN_ACTIONABLE_STAGES } from '@/features/admin/constants/onboardingStages';
import { BrandLogo } from '@/shared/components/BrandLogo';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { useAdminBookingStats } from '@/features/admin-bookings/hooks/useAdminBookings';
import { adminGrowthApi, adminGrowthKeys } from '@/features/admin-growth/api/admin-growth.api';

const DRAWER_WIDTH = 260;

interface AdminNavItem {
  label: string;
  path: string;
  icon: ReactNode;
  /** Whether a pathname belongs to this item. Defaults to a prefix match. */
  match?: (pathname: string) => boolean;
  /** Which count of work waiting on the admin to show as a badge. */
  badge?: 'onboarding' | 'bookings' | 'growth';
}

/**
 * `match` is used by both the sidebar highlight and the AppBar title, which
 * previously derived the active item with two different rules that could
 * disagree (a guide review page highlighted one item and titled another).
 */
const navItems: AdminNavItem[] = [
  {
    label: 'Overview',
    path: ADMIN_ROUTES.DASHBOARD,
    icon: <DashboardIcon />,
    match: (pathname) => pathname === ADMIN_ROUTES.DASHBOARD,
  },
  {
    label: 'Guide Onboarding',
    path: ADMIN_ROUTES.ONBOARDING,
    icon: <VerifiedUserIcon />,
    badge: 'onboarding',
  },
  { label: 'Travelers', path: ADMIN_ROUTES.TRAVELERS, icon: <PeopleIcon /> },
  {
    label: 'All Guides',
    path: ADMIN_ROUTES.GUIDES,
    icon: <SupervisorAccountIcon />,
    match: (pathname) =>
      pathname === ADMIN_ROUTES.GUIDES ||
      /^\/admin\/guides\/[^/]+\/review$/.test(pathname),
  },
  { label: 'Bookings', path: ADMIN_ROUTES.BOOKINGS, icon: <EventNoteIcon />, badge: 'bookings' },
  { label: 'Live Cities', path: ADMIN_ROUTES.LIVE_CITIES, icon: <LocationCityIcon /> },
  { label: 'Blog Management', path: ADMIN_ROUTES.BLOGS, icon: <ArticleIcon /> },
  {
    label: 'Finance',
    path: ADMIN_ROUTES.FINANCE,
    icon: <AccountBalanceIcon />,
    match: (pathname) =>
      pathname.startsWith(ADMIN_ROUTES.FINANCE) ||
      pathname.startsWith(ADMIN_ROUTES.DISPUTES),
  },
  {
    label: 'Growth & Rewards',
    path: ADMIN_ROUTES.GROWTH,
    icon: <EmojiEventsIcon />,
    badge: 'growth',
  },
  { label: 'Settings', path: ADMIN_ROUTES.SETTINGS, icon: <SettingsIcon /> },
];

function matchesNavItem(item: AdminNavItem, pathname: string): boolean {
  return item.match ? item.match(pathname) : pathname.startsWith(item.path);
}

/**
 * Work the admin can act on right now. Deliberately excludes the stages where
 * the ball is with the guide or with SpringVerify — a badge counting those
 * would never reach zero and would stop meaning anything.
 */
function useAdminActionableCount(): number {
  const { data } = useQuery({
    queryKey: adminKeys.onboarding({ limit: 1 }),
    queryFn: () => adminApi.getOnboardingPipeline({ limit: 1 }),
    staleTime: 60_000,
  });

  if (!data) return 0;
  return ADMIN_ACTIONABLE_STAGES.reduce(
    (total, stage) => total + (data.counts[stage] ?? 0),
    0
  );
}

/** Rewards awaiting approval plus photos waiting for review. */
function useAdminGrowthAttention(): number {
  const { data } = useQuery({
    queryKey: adminGrowthKeys.attention(),
    queryFn: adminGrowthApi.attention,
    staleTime: 60_000,
  });
  return data?.count ?? 0;
}

import { sidebarDrawerPaperSx, dashboardAppBarSx, dashboardMainSx } from '@/shared/styles/surfaces';

function AdminNavList({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const badgeCounts = {
    onboarding: useAdminActionableCount(),
    bookings: useAdminBookingStats().data?.needsAttention ?? 0,
    growth: useAdminGrowthAttention(),
  };

  return (
    <List sx={{ px: 1.5, py: 1.5, flex: 1 }}>
      {navItems.map((item) => {
        const selected = matchesNavItem(item, location.pathname);
        const badge = item.badge ? badgeCounts[item.badge] : 0;

        return (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={selected}
              onClick={onNavigate}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: selected ? 700 : 500, fontSize: '0.9rem' }}
              />
              {badge > 0 && (
                <Chip
                  label={badge}
                  size="small"
                  color="primary"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              )}
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

export function AdminDashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } finally {
      logout();
      navigate('/', { replace: true });
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Box>
          <BrandLogo size="md" surface="dark" />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', display: 'block', mt: 0.75 }}>
            Admin Console
          </Typography>
        </Box>
      </Box>

      <Divider />

      <AdminNavList onNavigate={() => setMobileOpen(false)} />

      <Divider />

      <Box sx={{ p: 2 }}>
        <ListItemButton component={RouterLink} to="/" sx={{ mb: 1 }}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <HomeIcon />
          </ListItemIcon>
          <ListItemText primary="Back to Site" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, ...sidebarDrawerPaperSx },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, ...sidebarDrawerPaperSx },
        }}
        open
      >
        {drawer}
      </Drawer>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} sx={dashboardAppBarSx}>
          <Toolbar sx={{ gap: 2 }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' } }}
              aria-label="Open admin menu"
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1, fontSize: '1rem' }}>
              {navItems.find((item) => matchesNavItem(item, location.pathname))
                ?.label ?? 'Admin'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <NotificationBell />
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                {(user?.firstName?.[0] ?? user?.phone?.[0] ?? 'A').toUpperCase()}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                  {user?.firstName ?? user?.phone ?? 'Admin'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Super Admin
                </Typography>
              </Box>
              <IconButton onClick={handleLogout} aria-label="Sign out" size="small">
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ ...dashboardMainSx, p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
